import {
  add,
  differenceInDays,
  differenceInHours,
  differenceInMilliseconds,
  differenceInMinutes,
  differenceInMonths,
  differenceInSeconds,
  differenceInYears,
  Duration,
  format,
  isAfter,
  isBefore,
  parseISO,
  set,
  sub,
} from 'date-fns';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';

import { DateTime, DateTimePartsEnum } from './avio-date-time.model';
import { DATE_TIME_FORMATS } from './formats.constants';

const isIsoTimeStringRegex = new RegExp(/Z$/, 'i');

// ====== GETTING ======
// Written a separate function for this so we can always change the implementation when needed
export const getNow = () => new Date();

export const getYesterday = () => {
  return subtractDate(getNow(), { days: 1 });
};

// ====== PARSING ======
// Created a custom parse function so we can also change the implementation when needed
// Date fns also parses internally but this gives us more flexibility
export const parseDate = (date: DateTime): Date => {
  if (date instanceof Date) {
    return date;
  }
  if (typeof date === 'string' && !isIsoTimeStringRegex.test(date)) {
    return parseISO(date);
  }
  return new Date(date);
};

// ====== FORMATTING ======
export const formatUsingTimeZone = (date: DateTime, timeFormat: string, formatString: string) => {
  const parsedDate = parseDate(date);

  // https://github.com/date-fns/tz/issues/32
  // https://github.com/facebook/hermes/issues/1100
  if (timeFormat === 'Asia/Kolkata') {
    return formatInTimeZone(parsedDate, 'Asia/Calcutta', formatString);
  }

  return formatInTimeZone(parsedDate, timeFormat, formatString);
};

export const formatDate = (date: DateTime, formatString: string, useUtc: boolean = false): string => {
  const parsedDate = parseDate(date);
  if (useUtc) {
    return formatUsingTimeZone(parsedDate, 'UTC', formatString);
  }
  return format(parsedDate, formatString);
};

export const formatDateTimeLongNoZone = (date: DateTime, useUtc: boolean = false): string => {
  const parsedDate = parseDate(date);
  return formatDate(parsedDate, DATE_TIME_FORMATS.DATE_TIME_LONG_NO_ZONE_FORMAT, useUtc);
};

export const formatTime = (date: DateTime, useUtc: boolean = false) => {
  const parsedDate = parseDate(date);
  return formatDate(parsedDate, DATE_TIME_FORMATS.TIME, useUtc);
};

export const formatShortDate = (date: DateTime) => {
  const parsedDate = parseDate(date);
  return format(parsedDate, DATE_TIME_FORMATS.SHORT_DATE);
};

export const formatLongDate = (date: DateTime, useUtc: boolean = false) => {
  const parsedDate = parseDate(date);
  return formatDate(parsedDate, DATE_TIME_FORMATS.DATE_LONG, useUtc);
};

export const formatTimeDate = (date: DateTime) => {
  const parsedDate = parseDate(date);
  return format(parsedDate, DATE_TIME_FORMATS.TIME_DATE);
};

// ====== CALCULATIONS & MANIPULATIONS  ======
export const isValidDate = (date: DateTime) => {
  return !Number.isNaN(parseDate(date).valueOf());
};

export const addDate = (date: DateTime, addParams: Duration): Date => {
  const parsedDate = parseDate(date);
  return add(parsedDate, addParams);
};
export const subtractDate = (date: DateTime, subtractParams: Duration): Date => {
  const parsedDate = parseDate(date);
  return sub(parsedDate, subtractParams);
};

export const isBetween = (date: DateTime, firstDate: DateTime, secondDate: DateTime): boolean => {
  const parsedDate = parseDate(date);
  const parsedFirstDate = parseDate(firstDate);
  const parsedSecondDate = parseDate(secondDate);
  return (
    (isAfter(parsedDate, parsedFirstDate) && isBefore(parsedDate, parsedSecondDate)) ||
    (isAfter(parsedDate, parsedSecondDate) && isBefore(parsedDate, parsedFirstDate))
  );
};

export const isAfterDateTime = (date: DateTime, dateToCompare: DateTime) => {
  const parsedDate = parseDate(date);
  const parsedDateToCompare = parseDate(dateToCompare);
  return isAfter(parsedDate, parsedDateToCompare);
};

export const splitTimeString = (time: string) =>
  time.split(':').map(t => {
    const numericValue = parseInt(t);
    return Number.isInteger(numericValue) ? numericValue : 0;
  });

export const setUTCHoursAndMinutes = (date: DateTime, time: string) => {
  const parsedDate = parseDate(date);
  const splitTime = splitTimeString(time);

  return fromZonedTime(
    set(parsedDate, {
      hours: splitTime[0],
      minutes: splitTime[1],
    }),
    'UTC',
  );
};

export const setLocalHoursAndMinutes = (date: DateTime, time: string) => {
  const parsedDate = parseDate(date);
  const splitTime = splitTimeString(time);

  return set(parsedDate, {
    date: parsedDate.getUTCDate(),
    hours: splitTime[0],
    minutes: splitTime[1],
    month: parsedDate.getUTCMonth(),
    year: parsedDate.getUTCFullYear(),
  });
};

export const dateDiff = (date1: DateTime, date2: DateTime, parts: DateTimePartsEnum) => {
  const parsedDate1 = parseDate(date1);
  const parsedDate2 = parseDate(date2);
  let diffValue = 0;

  switch (parts) {
    case DateTimePartsEnum.YEAR:
      diffValue = differenceInYears(parsedDate1, parsedDate2);
      break;
    case DateTimePartsEnum.MONTH:
      diffValue = differenceInMonths(parsedDate1, parsedDate2);
      break;
    case DateTimePartsEnum.DAY:
      diffValue = differenceInDays(parsedDate1, parsedDate2);
      break;
    case DateTimePartsEnum.HOUR:
      diffValue = differenceInHours(parsedDate1, parsedDate2);
      break;
    case DateTimePartsEnum.MINUTE:
      diffValue = differenceInMinutes(parsedDate1, parsedDate2);
      break;
    case DateTimePartsEnum.SECOND:
      diffValue = differenceInSeconds(parsedDate1, parsedDate2);
      break;
    case DateTimePartsEnum.MILLISECOND:
      diffValue = differenceInMilliseconds(parsedDate1, parsedDate2);
      break;
  }

  return isBefore(date2, date1) ? -diffValue : diffValue;
};

export const getHoursFromSeconds = (seconds: number): number => {
  const totalHours = seconds / 3600;

  return Number.isInteger(totalHours) ? totalHours : Math.round(totalHours * 100) / 100;
};

export const getSecondsFromHours = (hours: number): number => {
  const seconds = hours * 3600;

  return seconds;
};
